import { DynamicElementMixin } from "%/dynamic-components/render-engine/mixins/production/dynamic-element-mixin";
import { ElementEditMixin } from "%/dynamic-components/builder-engine/mixins/element-edit-mixin";
import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import {
  LayoutElementMixinProduction,
} from "%/dynamic-components/render-engine/components/production/elements/layouts/layout-element-mixin-production";
import {
  ChildTrackingMixin,
} from "%/dynamic-components/render-engine/components/production/elements/layouts/child-tracking-changes-mixin";
import BorderSides from "%/dynamic-components/render-engine/options/border-options/border-sides";
import ClickAction from "%/dynamic-components/render-engine/options/click-actions/click-action";
import { mapActions } from "pinia";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";

export const LayoutElementMixin = {
  props: {
    name: {
      type: String,
      default: "",
      required: false,
    },
  },
  mixins: [
    LayoutElementMixinProduction,
    DynamicElementMixin,
    ElementEditMixin,
    ChildTrackingMixin,
  ],
  elementPanelSettings: {
    displayIcon: "layout",
    elementType: "layout",
  },
  optionBundles: [],
  options: [
    BorderSides,
    ClickAction,
  ],
  inject: {
    inBuilderEditScope: { from: "inBuilderEditScope", default: false },
  },
  computed: {
    myHeight() {
      const height = this.getOptionValue(LayoutHeight);
      if (height !== "fill" || this.inBuilderEditScope) {
        return height;
      }
      return "fillv2";
    },
    isTemplatedComponent() {
      return useDynamicComponentBuilderStore().isElementTemplatedComponent(this.identifier);
    },
    isEmpty() {
      return this.dynamicComponentInstance.getElementChildren(this.identifier).length === 0;
    },
    isRootLayout() {
      return this.dynamicComponentInstance.rootElementId === this.identifier;
    },
    myEventListeners() {
      return {
        click: event => {
          event.stopPropagation();
          this.internalHandleOnEdit(event);
        },
        mouseover: event => {
          event.stopPropagation();
          this.internalMouseOverHandler();
        },
        mouseleave: event => {
          event.stopPropagation();
          this.internalMouseLeaveHandler();
        },
        dragstart: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragStart(event);
        },
        dragend: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragEnd(event);
        },
        dragover: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragOver(event);
        },
        drop: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDrop(event);
        },
        dragleave: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragLeave(event);
        },
        contextmenu: event => {
          if (this.editMode) {
            event.stopPropagation();
            event.preventDefault();
            this.openContextualMenuForBuilder(event);
          }
        },
      };
    },
    contextMenuAttrs() {
      const configuration = { ...this.dynamicComponentInstance.getElementById(this.identifier) };
      return {
        style: this.contextMenuFixedPositionStyle,
        canCopy: true,
        canPaste: true,
        isDeletable: this.deletable(this.identifier, configuration) && !this.isRootLayout,
        isDuplicatable: this.duplicatable(this.identifier, configuration) && !this.isRootLayout,
        isEditable: this.editable(this.identifier, configuration),
        canWrapInHorizontalContainer: !this.isRootLayout && this.wrappable(this.identifier, configuration),
        canWrapInVerticalContainer: !this.isRootLayout && this.wrappable(this.identifier, configuration),
        canWrapSelectionInContainer: this.canWrapCurrentSelection(),
        canCreateCustomComponent: !this.isDynamicAppComponent && this.canCreateTemplateFrom(this.identifier, configuration),
        isTemplatedComponent: !this.isDynamicAppComponent && this.isTemplatedComponent,
        name: this.name,
        themeId: 0,
      };
    },
    contextMenuEvents() {
      return {
        close: () => this.setShowContextualMenuForIdInBuilder(null),
        delete: this.internalHandleOnDelete,
        duplicate: this.internalHandleDuplicate,
        edit: this.internalHandleOnEdit,
        "wrap-in-horizontal-container": this.internalWrapInHorizontalContainer,
        "wrap-in-vertical-container": this.internalWrapInVerticalContainer,
        "wrap-selection-in-vertical-container": this.internalWrapSelectionInVerticalContainer,
        "wrap-selection-in-horizontal-container": this.internalWrapSelectionInHorizontalContainer,
        "create-custom-component": this.internalCreateCustomComponent,
        "create-new-template-from-here": this.internalCreateDynamicComponentFromIdentifier,
        copy: this.internalHandleCopy,
        paste: this.internalHandlePaste,
      };
    },
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, {
      "deletable": "isDeletable",
      "editable": "isEditable",
      "duplicatable": "isDuplicatable",
      "wrappable": "canWrap",
      "canCreateTemplateFrom": "canCreateCustomComponent",
      "canWrapCurrentSelection": "canWrapCurrentSelection",
    }),
  },
};
